import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, Briefcase, TrendingUp, Phone, Mail, MapPin, 
  CheckCircle, ArrowLeft, Loader2, Award, Target, Handshake,
  Eye, EyeOff, Lock, UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LavoraConNoiPage = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    ragione_sociale: '',
    email: '',
    telefono: '',
    citta: '',
    piva: '',
    password: '',
    conferma_password: '',
    is_collaboratore: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome || !formData.email || !formData.telefono || !formData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    if (formData.password !== formData.conferma_password) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }

    if (!acceptTerms) {
      toast.error('Devi confermare la candidatura come collaboratore');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/public/register`, formData);
      
      // NESSUN AUTO-LOGIN - mostra messaggio di attesa
      if (response.data.pending) {
        setSubmitted(true);
        toast.success('Candidatura inviata con successo!');
      } else if (response.data.access_token) {
        // Solo per il primo admin
        setAuth(response.data.access_token, response.data.user);
        toast.success('Registrazione completata! Benvenuto!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setSubmitting(false);
    }
  };

  // Success message after submission - SEMPRE mostrato per candidature agenti
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-2 border-emerald-200">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Candidatura Inviata!
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Grazie per la tua candidatura.<br />
              <strong>La tua richiesta è in fase di valutazione.</strong><br />
              Riceverai un&apos;email non appena il tuo profilo sarà attivato.
            </p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full" variant="outline">
                  Torna al Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' }}>
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/login" className="flex items-center gap-2 text-white/80 hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Torna al Login</span>
            </Link>
            <img 
              src="/assets/logos/logo-light.png" 
              alt="MAcheNoleggio" 
              className="h-10 w-auto"
              onError={(e) => e.target.style.display = 'none'}
            />
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-20 px-4 text-center text-white">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full mb-6 border border-primary/30">
            <Briefcase className="w-4 h-4" />
            <span className="text-sm font-medium">Network Agenti</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Entra nel Network di MAcheNoleggio
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
            Diventa un punto di riferimento per la mobilità nella tua zona.
          </p>
        </div>
      </section>

      {/* Benefits Section - Glassmorphism */}
      <section className="py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
            {/* Box 1 - Provvigioni */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-primary/10 rounded-xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
              <div className="relative bg-white/5 backdrop-blur-xl rounded-xl p-6 text-center border border-white/20 hover:border-primary/40 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-400/30">
                  <TrendingUp className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Provvigioni ai vertici</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Massimizza i tuoi guadagni con un piano provvigionale chiaro, meritocratico e senza sorprese.
                </p>
              </div>
            </div>

            {/* Box 2 - Supporto */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-primary/10 rounded-xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
              <div className="relative bg-white/5 backdrop-blur-xl rounded-xl p-6 text-center border border-white/20 hover:border-primary/40 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500/30 to-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-400/30">
                  <Target className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Supporto e Strumenti</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Ti forniamo la nostra tecnologia e il supporto costante per gestire i tuoi clienti in totale semplicità.
                </p>
              </div>
            </div>

            {/* Box 3 - Libertà */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-primary/10 rounded-xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
              <div className="relative bg-white/5 backdrop-blur-xl rounded-xl p-6 text-center border border-white/20 hover:border-primary/40 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500/30 to-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-amber-400/30">
                  <Handshake className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Libertà Operativa</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Gestisci la tua attività in autonomia, con la forza di un partner solido alle spalle.
                </p>
              </div>
            </div>
          </div>

          {/* Messaggio Top Player */}
          <div className="text-center mb-8">
            <p className="text-slate-400 italic text-sm sm:text-base max-w-2xl mx-auto">
              &ldquo;Hai già un portafoglio clienti attivo? <a href="mailto:preventivi@machenoleggio.it" className="text-primary hover:underline">Contattaci</a> per definire un piano di collaborazione personalizzato.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-8 px-4 pb-16">
        <div className="max-w-lg mx-auto">
          <Card className="shadow-2xl border-0 bg-white">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl sm:text-2xl flex items-center justify-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                <UserPlus className="w-6 h-6 text-primary" />
                Candidati Ora
              </CardTitle>
              <CardDescription>
                Compila il modulo per unirti al network
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome e Cognome */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      placeholder="Il tuo nome"
                      className="h-12"
                      autoComplete="off"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleChange}
                      placeholder="Il tuo cognome"
                      className="h-12"
                      autoComplete="off"
                      required
                    />
                  </div>
                </div>

                {/* Ragione Sociale */}
                <div className="space-y-2">
                  <Label htmlFor="ragione_sociale">Ragione Sociale</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="ragione_sociale"
                      name="ragione_sociale"
                      data-testid="ragione-sociale-input"
                      value={formData.ragione_sociale}
                      onChange={handleChange}
                      placeholder="Es. Rossi Auto SRL"
                      className="h-12 pl-10"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@esempio.com"
                      className="h-12 pl-10"
                      autoComplete="off"
                      required
                    />
                  </div>
                </div>

                {/* Telefono */}
                <div className="space-y-2">
                  <Label htmlFor="telefono">Telefono *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="telefono"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="+39 333 1234567"
                      className="h-12 pl-10"
                      autoComplete="off"
                      required
                    />
                  </div>
                </div>

                {/* Città */}
                <div className="space-y-2">
                  <Label htmlFor="citta">Città / Zona di Lavoro</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="citta"
                      name="citta"
                      value={formData.citta}
                      onChange={handleChange}
                      placeholder="Es. Roma, Latina, Velletri..."
                      className="h-12 pl-10"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Partita IVA */}
                <div className="space-y-2">
                  <Label htmlFor="piva">Partita IVA</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="piva"
                      name="piva"
                      value={formData.piva}
                      onChange={handleChange}
                      placeholder="Es. 12345678901"
                      className="h-12 pl-10"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Minimo 6 caratteri"
                      className="h-12 pl-10 pr-10"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Conferma Password */}
                <div className="space-y-2">
                  <Label htmlFor="conferma_password">Conferma Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="conferma_password"
                      name="conferma_password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.conferma_password}
                      onChange={handleChange}
                      placeholder="Ripeti la password"
                      className="h-12 pl-10 pr-10"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Checkbox Collaboratore */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="collaboratore"
                      checked={acceptTerms}
                      onCheckedChange={(checked) => setAcceptTerms(checked)}
                      className="mt-1"
                    />
                    <label htmlFor="collaboratore" className="text-sm cursor-pointer">
                      <span className="font-semibold text-slate-800">Desidero candidarmi come Collaboratore/Agente</span>
                      <p className="text-slate-500 mt-1">
                        Confermo di voler entrare nella rete di agenti MAcheNoleggio e di essere disponibile per un colloquio conoscitivo.
                      </p>
                    </label>
                  </div>
                </div>

                {/* Info */}
                <div className="text-xs text-slate-500 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <strong>Nota:</strong> Dopo la registrazione, la tua candidatura sarà valutata. 
                  Una volta approvata, riceverai un&apos;email di conferma per accedere alla tua area riservata.
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full h-14 text-base font-semibold"
                  disabled={submitting || !acceptTerms}
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Registrazione in corso...</>
                  ) : (
                    <><UserPlus className="w-5 h-5 mr-2" />Registrati e Inizia</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer link */}
          <p className="text-center text-slate-400 text-sm mt-6">
            Hai già un account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Accedi
            </Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-sm">
            MAcheNoleggio è un marchio di proprietà di MAAC FINANCE SRL
          </p>
          <p className="text-slate-600 text-xs mt-2">
            © {new Date().getFullYear()} Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LavoraConNoiPage;
